import {useCallback, useEffect} from 'react';

import {useRouter} from 'next/router';

import {wait} from 'UI/utils/helpers';
import {stableBody} from 'UI/utils/stableBody';

/**
 * A custom React hook that enables automatic scrolling to anchor elements after cross-page navigation.
 * This hook listens for Next.js router events and automatically scrolls to elements with IDs matching
 * the URL hash fragment when navigating between pages. It uses DOM observers to wait for elements to
 * be available in the DOM before attempting to scroll, ensuring reliable behavior even with dynamically
 * loaded content.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *     useRouterCrossPageScroll();
 *
 *     return <div>Component content</div>;
 * };
 * ```
 */
export const useRouterCrossPageScroll = () => {
    const router = useRouter();

    /**
     * A callback function that asynchronously handles scrolling to anchor elements after route changes.
     * This function sets up DOM observation to detect when target elements become available in the DOM
     * and automatically scrolls them into view. It uses MutationObserver to watch for DOM changes and
     * ResizeObserver to ensure proper scrolling after layout adjustments.
     *
     * @param url     The target URL that was navigated to.
     * @param shallow A boolean indicating whether the navigation was a shallow route change.
     * @returns A promise that resolves when the scroll operation is complete or no action is needed.
     *
     * @example
     * ```tsx
     * await asyncScrollIntoView('/page#section', false);
     * ```
     */
    const asyncScrollIntoView = useCallback(async (url: string, shallow: boolean) => {
        if (window.location.hash && !shallow) {
            let section: HTMLElement | null = null;

            do {
                // eslint-disable-next-line no-await-in-loop
                await wait(10);

                section = document.querySelector(window.location.hash);
            } while (section === null);

            await stableBody();
            section.scrollIntoView();
        }
    }, []);

    /**
     * A callback function that handles the completion of route changes in Next.js router.
     * This function is triggered when a route change is completed and initiates the scroll-to-anchor
     * functionality by calling asyncScrollIntoView with the new URL and shallow navigation flag.
     *
     * @param url           The URL that was navigated to.
     * @param props         The route change options object.
     * @param props.shallow A boolean indicating whether the navigation was a shallow route change.
     * @returns Void This function does not return any value.
     *
     * @example
     * ```tsx
     * router.events.on('routeChangeComplete', handleRouteChangeEnd);
     * ```
     */
    const handleRouteChangeEnd = useCallback((url: string, {shallow}: {shallow: boolean}) => {
        void asyncScrollIntoView(url, shallow);
    }, [asyncScrollIntoView]);

    useEffect(() => {
        router.events.on('routeChangeComplete', handleRouteChangeEnd);

        return () => {
            router.events.off('routeChangeComplete', handleRouteChangeEnd);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router, handleRouteChangeEnd]);
};